import React, { useEffect, useRef, useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useErrorSnackbar } from '../../../../hooks/use-error-snackbar';
import { CitizenLookup } from '../../../../components/lookups/citizen-lookup/citizen-lookup';
import { ICitizenLookup } from '../../../../components/lookups/citizen-lookup/interfaces';
import { CreateChatDialog, ICreateChatResult, ISelectableItem } from '../../../citizen/common/communication/forum-chat/create-chat-dialog';
import { toAddChatRequest, toSelectableItems } from '../../../citizen/common/communication/forum-chat/new-chat-request';

const client = moduleClientFactory.createForumCaseworkerClient();

export interface IForumNewChatDialogProps {
  open: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

export function ForumNewChatDialog(props: IForumNewChatDialogProps) {
  const { open, onCreated, onCancel } = props;

  const [citizenId, setCitizenId] = useState('');
  const [selectableCases, setSelectableCases] = useState<ISelectableItem[]>([]);
  const [selectableContacts, setSelectableContacts] = useState<ISelectableItem[]>([]);
  const [confidentialChatsEnabled, setConfidentialChatsEnabled] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const latestOptionsRequest = useRef(0);

  useErrorSnackbar(error);

  useEffect(() => {
    if (!open) {
      return;
    }
    latestOptionsRequest.current++;
    setCitizenId('');
    setSelectableCases([]);
    setSelectableContacts([]);
    setError(undefined);
    client
      .getContext()
      .then(result => setConfidentialChatsEnabled(result.context?.addons?.isConfidentialChatsEnabled ?? false))
      .catch(setError);
  }, [open]);

  async function handleCitizenChange(citizen?: ICitizenLookup) {
    const chosenCitizenId = citizen?.citizenId ?? '';
    const request = ++latestOptionsRequest.current;
    setCitizenId(chosenCitizenId);
    setSelectableCases([]);
    setSelectableContacts([]);
    if (!chosenCitizenId) {
      return;
    }
    try {
      setError(undefined);
      const options = await client.getChatCreationOptions({ citizenId: chosenCitizenId });
      if (request === latestOptionsRequest.current) {
        setSelectableCases(toSelectableItems(options.cases));
        setSelectableContacts(toSelectableItems(options.contacts));
      }
    } catch (fetchError) {
      if (request === latestOptionsRequest.current) {
        setError(fetchError);
      }
    }
  }

  async function handleCreateChat(result: ICreateChatResult) {
    try {
      setError(undefined);
      await client.addChat(await toAddChatRequest(citizenId, result));
      onCreated();
    } catch (createError) {
      // Keep the dialog open with the caseworker's text so the message is not lost.
      setError(createError);
    }
  }

  return (
    <CreateChatDialog
      open={open}
      selectableCases={selectableCases}
      selectableContacts={selectableContacts}
      confidentialChatsEnabled={confidentialChatsEnabled}
      citizenPicker={<CitizenLookup label="Borger" variant="outlined" value={citizenId} required onChange={handleCitizenChange} />}
      hasChosenCitizen={!!citizenId}
      onSave={handleCreateChat}
      onCancel={onCancel}
    />
  );
}
